import { useEffect } from 'react'

import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { ErrorRouteComponent, useRouter } from '@tanstack/react-router'

import { Alert, Button, IconName, ListItem, Tooltip, TooltipContent, TooltipTrigger } from '@forto-dev/phoenix-ui-core'
import { Stack } from '@forto-dev/phoenix-ui-core/common'
import { Splash } from '@forto-dev/phoenix-ui-core/ship'

import { useLocalisation } from '@/utils/localisation'

/**
 * from https://tanstack.com/router/latest/docs/framework/react/guide/external-data-loading#error-handling-with-tanstack-query
 */

export const createShipmentContextErrorFallback: ({
  icon,
  testId,
  message,
}: {
  icon: IconName
  testId?: string
  message: string
}) => ErrorRouteComponent =
  ({ icon, testId, message }) =>
  ({ error }) => {
    const queryErrorResetBoundary = useQueryErrorResetBoundary()

    useEffect(() => {
      queryErrorResetBoundary.reset()
    }, [queryErrorResetBoundary])

    return (
      <Tooltip>
        <TooltipTrigger>
          <ListItem icon={icon} disabled data-testid={testId}>
            {message}
          </ListItem>
        </TooltipTrigger>
        <TooltipContent>{error.message}</TooltipContent>
      </Tooltip>
    )
  }

export const createErrorFallback: (message: string) => ErrorRouteComponent =
  (message: string) =>
  ({ error, reset }) => {
    const router = useRouter()
    const queryErrorResetBoundary = useQueryErrorResetBoundary()
    const { translate } = useLocalisation()

    useEffect(() => {
      // Reset the query error boundary
      queryErrorResetBoundary.reset()
    }, [queryErrorResetBoundary])

    return (
      <Stack gap={16}>
        <Alert variant="negative" title={message} description={error.message} />
        <Button
          onClick={() => {
            // Reset the router error boundary
            reset()
            // Invalidate the route to reload the loader
            router.invalidate()
          }}
        >
          {translate('actions.retry')}
        </Button>
      </Stack>
    )
  }

export const createAppErrorFallback: () => ErrorRouteComponent =
  () =>
  ({ error, reset }) => {
    const router = useRouter()
    const queryErrorResetBoundary = useQueryErrorResetBoundary()
    const { translate } = useLocalisation()

    useEffect(() => {
      // Reset the query error boundary
      queryErrorResetBoundary.reset()
    }, [queryErrorResetBoundary])

    return (
      <Splash>
        <Stack gap={16} x="center">
          <Alert variant="negative" title={error.message} description={error.message} />
          <Button
            onClick={() => {
              // Reset the router error boundary
              reset()
              // Invalidate the route to reload the loader
              router.invalidate()
            }}
          >
            {translate('actions.retry')}
          </Button>
        </Stack>
      </Splash>
    )
  }
